// Caps the LCS table (4 bytes per cell); larger rewrites show as one removal plus one insertion.
const MAX_CELLS = 4_000_000;

export type DiffPart = { kind: "same" | "removed" | "added"; text: string };

/** Word-level diff via LCS; tokens keep their whitespace so the text rebuilds exactly. */
export const wordDiff = (before: string, after: string): DiffPart[] => {
  const a = before.match(/\s+|[^\s]+/g) ?? [];
  const b = after.match(/\s+|[^\s]+/g) ?? [];
  // Trim the common head and tail so the table stays small for local edits.
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head++;
  let tail = 0;
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  )
    tail++;
  const x = a.slice(head, a.length - tail);
  const y = b.slice(head, b.length - tail);
  const n = x.length;
  const m = y.length;

  const parts: DiffPart[] = [];
  const push = (kind: DiffPart["kind"], text: string) => {
    const last = parts[parts.length - 1];
    if (last && last.kind === kind) last.text += text;
    else parts.push({ kind, text });
  };
  if (head) push("same", a.slice(0, head).join(""));
  if (n * m > MAX_CELLS) {
    if (n) push("removed", x.join(""));
    if (m) push("added", y.join(""));
  } else {
    const t = new Uint32Array((n + 1) * (m + 1));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        t[i * (m + 1) + j] =
          x[i] === y[j]
            ? t[(i + 1) * (m + 1) + j + 1] + 1
            : Math.max(t[(i + 1) * (m + 1) + j], t[i * (m + 1) + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (x[i] === y[j]) {
        push("same", x[i]);
        i++;
        j++;
      } else if (t[(i + 1) * (m + 1) + j] >= t[i * (m + 1) + j + 1])
        push("removed", x[i++]);
      else push("added", y[j++]);
    }
    while (i < n) push("removed", x[i++]);
    while (j < m) push("added", y[j++]);
  }
  if (tail) push("same", a.slice(a.length - tail).join(""));
  return parts;
};
